/**
 * Loading and refresh for Patient Eligibility Mapping option catalogs.
 */

import { useCallback, useEffect, useState } from "react";
import { clinicalTrialsService } from "../api/clinicalTrialsService";
import type { EligibilityOptionsResponse } from "../data-model/eligibilityOptions";

/**
 * Abort the options request rather than spinning indefinitely. A stalled
 * dependency (e.g. an unreachable datastore) otherwise leaves the page with no
 * loader exit, so the timeout surfaces the retryable error state instead.
 */
const REQUEST_TIMEOUT_MS = 20000;

/** State returned by {@link useEligibilityOptions}. */
export interface EligibilityOptionsState {
  options: EligibilityOptionsResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * Fetch the eligibility options payload that drives the worklist header, tabs,
 * and filters.
 *
 * @param trialId - Selected trial used to request populated tab counts
 * @return EligibilityOptionsState: Options payload plus loading, error, and refresh
 */
export function useEligibilityOptions(trialId?: string): EligibilityOptionsState {
  const [options, setOptions] = useState<EligibilityOptionsResponse | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  /**
   * Re-request eligibility options from the service.
   *
   * @return void
   */
  const refresh = useCallback((): void => {
    setReloadToken((current) => current + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let timedOut = false;
    const timer = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, REQUEST_TIMEOUT_MS);
    setLoading(true);
    setError(null);
    clinicalTrialsService
      .getEligibilityOptions(trialId, controller.signal)
      .then((response) => {
        setOptions(response);
      })
      .catch((reason: unknown) => {
        // An abort without the timeout flag is this effect being cleaned up, so
        // the unmounted/superseded render must not publish state.
        if (controller.signal.aborted && !timedOut) return;
        if (timedOut) {
          setError("Eligibility options request timed out");
          return;
        }
        setError(
          reason instanceof Error
            ? reason.message
            : "Failed to load eligibility options",
        );
      })
      .finally(() => {
        window.clearTimeout(timer);
        if (timedOut || !controller.signal.aborted) setLoading(false);
      });
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [reloadToken, trialId]);

  return { options, loading, error, refresh };
}
